/**
 * Clause numbering typed into paragraph text.
 *
 * Contracts often carry their numbering as literal text at the start of the
 * paragraph ("3.7.1 ", "(a) ", "iv. ") rather than as Word auto-numbering. The
 * reader reports each paragraph's marker (`leadingClauseMarker`), which tells
 * the heading map on the core which body paragraphs are numbered clauses, and
 * the add-in sets it aside when it asks whether a landed redline emptied a
 * paragraph.
 */
export class ClauseMarker {
    /**
     * The marker at the start of `text`, trailing space included, or null when
     * the paragraph has none. Leading markers recognised, each followed by at
     * least one space:
     *   "1."  "3.7.1."  "3.7.1)"   dotted numbers closed by a dot or bracket
     *   "3.7.1"                     bare dotted numbers with two or more levels
     *   "a."  "B)"                  a single letter closed by a dot or bracket
     *   "iv."  "IX)"                roman numerals up to four characters
     *   "(a)"  "(12)"  "(iv)"       anything short in round brackets
     */
    static leading(text: string): string | null {
        const MARKER = new RegExp(
            "^(?:"
                + [
                    "\\d+(?:\\.[0-9a-zA-Z]+)*[.)]",
                    "\\d+(?:\\.\\d+)+",
                    "[a-zA-Z][.)]",
                    "[ivxlcdmIVXLCDM]{1,4}[.)]",
                    "\\([0-9a-zA-Z]{1,4}\\)",
                ].join("|")
                + ")\\s+",
        );
        const leadingClauseMarkerMatch = MARKER.exec(text);
        /**
         * EXAMPLE
         *   paragraph text = "3.7.1 Rights"
         *   exec(text)     -> ["3.7.1 ", ...]
         *   match[0]       -> full matched marker, trailing space included
         *
         * NULL
         *   No recognised leading clause marker at the start of the paragraph text.
         */
        return leadingClauseMarkerMatch ? leadingClauseMarkerMatch[0] : null;
    }
}
